import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Department,
  UserScopeNameEnum,
  useUsersSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import { getUsersLoadOptions as getUsersLoadOptionsFn } from 'utils/fetch/searchLists/usersSearchList';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import { createAdditionalFilters } from 'components/DepartmentsModalTableMultiselect/utils/createAdditionalFilters';
import { MAX_DEPARTMENT_HIERARCHY_DEPTH } from '../../constants';
import getDepartmentsTableColumns from '../../utils/getDepartmentsTableColumns';
import useDepartmentsTableTree from '../../utils/useDepartmentsTableTree';
import useDepartmentDescendantIds from '../../utils/useDepartmentDescendantIds';

type Props = {
  // The department currently being edited, if any — it and its whole
  // subtree are excluded from the pickable list, so a department can't be
  // re-parented under itself or one of its own descendants. Pre-empts the
  // BE's `cycle_detected` ("cannot be nested under its own descendant")
  // error client-side.
  excludeId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (department: Department | null) => void;
  selected?: string | null;
};

function DepartmentParentSelectModal({
  excludeId,
  isOpen,
  onClose,
  onSelect,
  selected,
}: Props) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const selectedIds = useMemo(() => (selected ? [selected] : []), [selected]);

  const filterStateDefault = useMemo(() => ({}), []);

  const {
    activeTab,
    activeTabState,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode: ModalTableMultiselectMode.WithTabs,
    selectedIds,
    filterStateDefault,
  });

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<Department>({
      isOpen,
      mode: ModalTableMultiselectMode.WithTabs,
      activeTab,
      isSelectedOnlyMode: false,
    });

  const additionalFilters = useMemo(() => {
    if (!filterState) return undefined;
    return createAdditionalFilters(filterState[0] || {});
  }, [filterState]);

  // All tab query
  const {
    collection,
    loading: departmentsLoading,
    firstLoading: departmentsFirstLoading,
    totalCount,
    fetchIds,
  } = useDepartmentsQueryHook({
    searchQuery: searchState?.[0] || '',
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    additionalFilters,
    skip: !shouldRunAllQuery,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: departmentsLoading,
      firstLoading: departmentsFirstLoading,
    });
  }, [
    collection,
    departmentsFirstLoading,
    departmentsLoading,
    handleAllTabQueryResult,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: departmentsFirstLoading,
  });

  // Departments are hierarchical, so the All tab browses them as an
  // expandable tree (root departments paginated, children lazy-loaded per
  // node), same as DepartmentsModalTableMultiselect. The current parent's
  // ancestors are auto-expanded so the preselected radio row is visible on
  // open instead of requiring the user to hunt through the hierarchy.
  const departmentsTableTree = useDepartmentsTableTree({
    collection: allTabCollection,
    searchQuery: searchState?.[0],
    autoExpandIds: isOpen && selected ? [selected] : [],
  });

  // Accumulate every department row seen (root + lazily-loaded children) for
  // the lifetime of the open modal. There's no reverse filter for "parent"
  // the way there is for e.g. task/intervention links, so both the Selected
  // tab and the row-click resolution below rely entirely on this cache —
  // same fallback DepartmentsModalTableMultiselect uses for its create flow.
  const knownDepartmentsRef = useRef<Map<string, Department>>(new Map());
  departmentsTableTree.data.forEach((item) => {
    knownDepartmentsRef.current.set(String(item.id), item);
  });
  useEffect(() => {
    if (!isOpen) knownDepartmentsRef.current.clear();
  }, [isOpen]);

  const collectionSelected = useMemo(() => {
    if (idsForSelectedTabQuery.length === 0) return [];
    return idsForSelectedTabQuery
      .map((id) => knownDepartmentsRef.current.get(id))
      .filter((item): item is Department => !!item);
    // `departmentsTableTree.data` isn't read directly, but the ref it
    // populates above is — this dependency is what makes the memo recompute
    // once newly-expanded rows land in the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsForSelectedTabQuery, departmentsTableTree.data]);

  const { descendantIds, subtreeDepth } = useDepartmentDescendantIds({
    departmentId: excludeId,
    skip: !isOpen || !excludeId,
  });

  // A candidate parent must leave enough headroom for the whole subtree
  // being moved, not just for the edited department itself — otherwise its
  // deepest descendant can end up past MAX_DEPARTMENT_HIERARCHY_DEPTH even
  // though the chosen parent row looked shallow enough on its own, and the
  // BE rejects the submit with `max_depth_exceeded`.
  const maxParentDepth = Math.max(
    0,
    MAX_DEPARTMENT_HIERARCHY_DEPTH - subtreeDepth,
  );

  const columns = getDepartmentsTableColumns({ t });

  // ModalTableMultiselect's onSelect only ever surfaces ids (it's built for
  // multi-select linking); resolve the full Department here from the row
  // cache above so the caller keeps getting the full item it needs for the
  // field's label, same as before this reuse.
  const handleSelect = (ids: string[]) => {
    const nextId = ids[0] || null;
    if (nextId === (selected || null)) return;
    onSelect(nextId ? knownDepartmentsRef.current.get(nextId) || null : null);
  };

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<Department>
      activeTabState={activeTabState}
      caption={t('department-select')}
      columns={columns}
      data={departmentsTableTree.data}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={[]}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={false}
      getIsRowDisabled={(department) =>
        (!!excludeId && String(department.id) === String(excludeId)) ||
        descendantIds.has(String(department.id))
      }
      isOpen={isOpen}
      loading={shouldRunAllQuery ? departmentsLoading : false}
      loadingSelected={isFetchingAllIds}
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onSelect={handleSelect}
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      radioSelect
      searchPleceholder={t('departments-search-placeholder')}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedIds}
      selectionLimit={1}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={collectionSelected.length}
      tree={{
        ...departmentsTableTree.tree,
        maxDepth: maxParentDepth,
        maxDepthTooltip: t('department-max-depth-tooltip'),
      }}
    />
  );
}

export default memo(DepartmentParentSelectModal);
